import { module as ModuleJS } from "modujs";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { debounce } from "lodash";

gsap.registerPlugin(ScrollTrigger);

export default class extends ModuleJS {
    constructor(m) {
        super(m);

        this.active = false;
        this.nav = this.$("nav")[0];
        this.buttons = this.$("button");
        this.menu = this.nav.querySelector("ul");

        // Get all the link elements within the menu.
        this.links = this.menu.querySelectorAll("a");

        // Get all the link elements with children within the menu.
        this.linksWithChildren = this.menu.querySelectorAll(
            ".menu-item-has-children > a, .page_item_has_children > a"
        );

        this.header = document.getElementById("header");
        this.fakeNavItems = this.header.querySelectorAll('[href="#"]');

        this.NAV_ACTIVE_CLASS = "is-nav-active";

        this.HEADER_SCROLLED_CLASS = "is-header-scrolled";
        this.SCROLLTRIGGER_POSITION = "350px";
        this.DURATION_IN = 0.6;
        this.DURATION_OUT = 0.3;
        this.EASE = "power1.inOut";
    }

    init() {
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.setHeaderHeightCSSVariable = this.setHeaderHeightCSSVariable.bind(this);
        this.swapMenu = this.swapMenu.bind(this);
        this.enableScrolledMode = this.enableScrolledMode.bind(this);
        this.disableScrolledMode = this.disableScrolledMode.bind(this);

        if (!this.menu.classList.contains("nav-menu")) {
            this.menu.classList.add("nav-menu");
        }

        this.scrollTrigger = ScrollTrigger.create({
            start: this.SCROLLTRIGGER_POSITION,
            end: this.SCROLLTRIGGER_POSITION,
            onEnter: this.enableScrolledMode,
            onEnterBack: this.disableScrolledMode,
        });

        this.bindEvents();
    }

    swapMenu(fn) {
        const tl = gsap.timeline();

        tl.to(header, {
            duration: this.DURATION_OUT,
            autoAlpha: 0,
            y: "-100%",
            snap: {
                x: 1,
                y: 1,
            },
            ease: this.EASE,
        })
            .add(fn)
            .to(header, {
                duration: this.DURATION_IN,
                autoAlpha: 1,
                y: "0%",
                snap: {
                    x: 1,
                    y: 1,
                },
                clearProps: "all",
                ease: this.EASE,
            });
    }

    getHeaderScrolledClass() {
        return this.HEADER_SCROLLED_CLASS;
    }

    enableScrolledMode() {
        this.swapMenu(() => {
            document.body.classList.add(this.HEADER_SCROLLED_CLASS);
        });
    }

    disableScrolledMode() {
        this.swapMenu(() => {
            document.body.classList.remove(this.HEADER_SCROLLED_CLASS);
        });
    }

    /** Menu height as padding to body */
    setHeaderHeightCSSVariable() {
        const isScrolled = document.body.classList.contains(
            this.HEADER_SCROLLED_CLASS
        );

        if (isScrolled)
            document.body.classList.remove(this.HEADER_SCROLLED_CLASS);

        const headerHeight = this.header.getBoundingClientRect().height;
        if (headerHeight) {
            document.documentElement.style.setProperty(
                "--header-height",
                headerHeight + "px"
            );
        }

        if (isScrolled) document.body.classList.add(this.HEADER_SCROLLED_CLASS);
    }

    onButtonClick(e) {
        this.toggle();
    }

    toggle() {
        if (this.active) {
            this.hide();
        } else {
            this.show();
        }
    }

    show() {
        this.active = true;
        document.documentElement.classList.add(this.NAV_ACTIVE_CLASS);

        for (const button of this.buttons) {
            button.setAttribute("aria-expanded", "true");
        }
    }

    hide() {
        this.active = false;
        document.documentElement.classList.remove(this.NAV_ACTIVE_CLASS);

        for (const button of this.buttons) {
            button.setAttribute("aria-expanded", "false");
        }
    }

    onDocumentClick(event) {
        const isClickInside = this.nav.contains(event.target);

        if (!isClickInside) {
            this.hide();
        }
    }

    bindEvents(el) {
        // Toggle the .toggled class and the aria-expanded value each time the button is clicked.
        for (const button of this.buttons) {
            button.addEventListener("click", this.onButtonClick, true);
        }

        // Remove the .toggled class and set aria-expanded to false when the user clicks outside the navigation.
        document.addEventListener("click", this.onDocumentClick);

        // Toggle focus each time a menu link is focused or blurred.
        for (const link of this.links) {
            link.addEventListener("focus", this.toggleFocus, true);
            link.addEventListener("blur", this.toggleFocus, true);
        }

        // Toggle focus each time a menu link with children receive a touch event.
        for (const link of this.linksWithChildren) {
            link.addEventListener("touchstart", this.toggleFocus, false);
        }

        document.addEventListener(
            "DOMContentLoaded",
            debounce(this.setHeaderHeightCSSVariable, 50)
        );
        window.onload = this.setHeaderHeightCSSVariable;
        window.addEventListener(
            "resize",
            debounce(this.setHeaderHeightCSSVariable, 50)
        );

        // Désactivation clics a[href="#"]
        this.fakeNavItems.forEach(function (navItem) {
            navItem.addEventListener("click", function (e) {
                e.preventDefault();
            });
        });
    }

    update(newContainer) {
        this.bindEvents(newContainer);
    }

    destroy(oldContainer) {
        // this.el.removeEventListener("click", this.onClick);
    }

    /**
     * Sets or removes .focus class on an element.
     */
    toggleFocus(event) {
        if (event.type === "focus" || event.type === "blur") {
            let self = this;
            // Move up through the ancestors of the current link until we hit .nav-menu.
            while (!self.classList.contains("nav-menu")) {
                // On li elements toggle the class .focus.
                if ("li" === self.tagName.toLowerCase()) {
                    self.classList.toggle("-focus");
                }
                self = self.parentNode;
            }
        }

        if (event.type === "touchstart") {
            const menuItem = this.parentNode;
            event.preventDefault();
            for (const link of menuItem.parentNode.children) {
                if (menuItem !== link) {
                    link.classList.remove("-focus");
                }
            }
            menuItem.classList.toggle("-focus");
        }
    }
}
